import type { PostHog } from 'posthog-js'
import { readAnalyticsConfig, type AnalyticsConfig } from './analytics-config'
import { startConversionTracking } from './conversions'
import { BORG_PROPS, entryArm, entryLander, trafficSource, watchFunnelEvents } from './funnel-events'
import { FUNNEL_PRODUCTION_ORIGIN, FUNNEL_SITE_ORIGIN } from './funnel-contract'

type Gtag = (...args: unknown[]) => void
export type AnalyticsWindow = Window & {
  navigator: Navigator & { globalPrivacyControl?: boolean }
  dataLayer?: unknown[]
  gtag?: Gtag
  __borgAnalyticsStarted?: boolean
}

// Section anchors scroll the same page. Only #models routes represent another view.
// Drop query strings so email addresses and questionnaire inputs cannot enter URLs.
export function analyticsUrl(raw: string): string {
  try {
    const url = new URL(raw)
    if (!['http:', 'https:'].includes(url.protocol)) return ''
    url.search = ''
    if (url.pathname !== '/' || !/^#models(?:-[a-z]+)?$/.test(url.hash)) url.hash = ''
    return url.href
  } catch {
    return ''
  }
}

function trackingAllowed(win: AnalyticsWindow): boolean {
  return !win.navigator.globalPrivacyControl && !['1', 'yes'].includes(win.navigator.doNotTrack ?? '')
}

// Staging PostHog needs its own explicit opt-in and dedicated CI project token.
// Inherited production analytics remain blocked; other preview origins stay dark.
function originAllowed(config: AnalyticsConfig, origin: string): boolean {
  if (origin === FUNNEL_PRODUCTION_ORIGIN) return !config.stagingPosthog
  // The normal staging hostname is private through Twingate. Its existing
  // public bucket allows a real public-to-private browser permission test.
  if (origin === 'https://getborg-com-static-staging.storage.googleapis.com') {
    return Boolean(config.stagingPosthog && config.posthogToken && !config.ga4Id)
  }
  if (origin !== FUNNEL_SITE_ORIGIN || config.ga4Id) return false
  return config.stagingPosthog ? Boolean(config.posthogToken) : Boolean(config.googleAdsId) && !config.posthogToken
}

interface Pageview {
  url: string
  title: string
  referrer: string
}

export async function startAnalytics(
  config: AnalyticsConfig,
  win: AnalyticsWindow,
  loadPosthog: () => Promise<PostHog> = () => import('posthog-js').then(module => module.default),
): Promise<void> {
  // Build flags are also gated in CI; this runtime boundary protects inherited
  // flags in staging and isolated previews, including copied funnel pages.
  if (!config.enabled || !originAllowed(config, win.location.origin) || !trackingAllowed(win) || win.__borgAnalyticsStarted) return
  win.__borgAnalyticsStarted = true
  let lastUrl = ''
  let posthog: Pick<PostHog, 'capture'> | undefined
  const pending: Pageview[] = []
  let storage: Storage | undefined
  try { storage = win.sessionStorage } catch { storage = undefined }
  // Non-personal source of this visit, on every PostHog event so the funnel
  // can be split by Google Ads vs everything else, by utm campaign and by the
  // lander and test arms that started the funnel.
  const lander = entryLander(win.location.pathname, storage), headline = entryArm(storage, 'borg-headline'), hero = entryArm(storage, 'borg-hero')
  const traffic = { ...trafficSource(win.location.search, storage), ...(lander ? { entry_lander: lander } : {}), ...(headline ? { entry_headline: headline } : {}), ...(hero ? { entry_hero: hero } : {}) }

  const googleTagId = config.ga4Id || config.googleAdsId
  if (googleTagId) {
    win.dataLayer ??= []
    // Google's documented gtag queue uses an Arguments object.
    // eslint-disable-next-line prefer-rest-params
    win.gtag ??= function () { win.dataLayer!.push(arguments) }
    // Advertising consent is granted for the Google Ads destination, approved
    // 2026-10-08. Conversion measurement needs ad storage and an unredacted
    // click id; personalization stays on only because Google couples it to the
    // same ad_storage grant. No consent banner is shown, per that decision.
    win.gtag('consent', 'default', {
      analytics_storage: 'granted', ad_storage: 'granted',
      ad_user_data: 'granted', ad_personalization: 'granted',
    })
    if (config.googleAdsId) {
      // Redaction strips the click id and would leave every conversion
      // unattributed. Passthrough keeps attribution when cookies are blocked.
      win.gtag('set', 'ads_data_redaction', false)
      win.gtag('set', 'url_passthrough', true)
    }
    win.gtag('js', new Date())
    for (const id of [config.ga4Id, config.googleAdsId].filter(Boolean)) {
      // Google signals stay off for GA4 reporting. The Ads destination needs them
      // on, otherwise conversions arrive without the click they belong to.
      const advertising = id === config.googleAdsId
      win.gtag('config', id, {
        send_page_view: false,
        allow_google_signals: advertising,
        allow_ad_personalization_signals: advertising,
        page_location: analyticsUrl(win.location.href),
        page_referrer: analyticsUrl(win.document.referrer),
      })
    }
    const existing = Array.from(win.document.scripts).some(script => {
      try { const url = new URL(script.src); return url.origin === 'https://www.googletagmanager.com' && url.pathname === '/gtag/js' } catch { return false }
    })
    if (!existing) {
      const script = win.document.createElement('script')
      script.async = true
      script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(googleTagId)}`
      win.document.head.append(script)
    }
    // The funnel bridge reports outcomes onto dataLayer; turn the ones that are
    // real conversions into Google Ads events.
    if (config.googleAdsId) startConversionTracking(config.googleAdsId, win.dataLayer!, win.gtag!)
  }

  function capturePosthog(view: Pageview) {
    posthog?.capture('$pageview', {
      $current_url: view.url, $pathname: new URL(view.url).pathname,
      $title: view.title, $referrer: view.referrer, ...traffic, ...BORG_PROPS,
    })
  }

  function pageview() {
    if (!trackingAllowed(win)) return
    const url = analyticsUrl(win.location.href)
    if (!url || url === lastUrl) return
    const view = { url, title: win.document.title, referrer: lastUrl || analyticsUrl(win.document.referrer) }
    lastUrl = url
    if (config.ga4Id) win.gtag?.('event', 'page_view', {
      send_to: config.ga4Id, page_location: view.url,
      page_title: view.title, page_referrer: view.referrer,
    })
    if (config.posthogToken) {
      if (posthog) capturePosthog(view)
      else pending.push(view)
    }
  }
  // Run after the site's route handler updates the title; hashchange + popstate
  // can fire for one navigation, and the normalized URL deduplicates them.
  const onRoute = () => queueMicrotask(pageview)
  win.addEventListener('hashchange', onRoute)
  win.addEventListener('popstate', onRoute)
  pageview()

  if (config.posthogToken) {
    try {
      const sdk = await loadPosthog()
      if (!trackingAllowed(win)) return
      sdk.init(config.posthogToken, {
        api_host: config.posthogHost,
        autocapture: false,
        capture_pageview: false,
        capture_pageleave: false,
        disable_session_recording: true,
        disable_surveys: true,
        advanced_disable_flags: true,
        person_profiles: 'never',
        respect_dnt: true,
        sanitize_properties(properties) {
          for (const key of ['$current_url', '$referrer', '$initial_current_url', '$initial_referrer', '$session_entry_url', '$session_entry_referrer']) {
            if (typeof properties[key] === 'string') properties[key] = analyticsUrl(properties[key])
          }
          return properties
        },
        loaded(instance) {
          if (!trackingAllowed(win)) return
          posthog = instance
          pending.splice(0).forEach(capturePosthog)
          // Every funnel step, already queued or still to come (funnel-events.ts).
          win.dataLayer ??= []
          watchFunnelEvents(win.dataLayer, (name, props) => instance.capture(name, {
            ...props, ...traffic, ...BORG_PROPS, $pathname: win.location.pathname,
          }))
        },
      })
    } catch {
      // An unavailable provider must not stop navigation or the other provider.
      console.warn('Borg: PostHog could not be loaded')
    }
  }
}

if (typeof window !== 'undefined') {
  // Copied pages load this module asynchronously and put their title in the
  // authored body. Wait for parsing before the initial pageview; the async script
  // itself never holds DOMContentLoaded or the authored rendering runtime.
  const start = () => { void startAnalytics(readAnalyticsConfig(import.meta.env), window as AnalyticsWindow) }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true })
  else start()
}
